import dayjs from 'dayjs';

// dayjs accepts string | number | Date | dayjs.Dayjs | null | undefined.
type DateInput = dayjs.ConfigType;

export const formatDate = (date: DateInput) => {
  return dayjs(date).format('MMM D, YYYY');
};

// property-service serializes its `Instant` fields (createdAt, updatedAt,
// lastOrderDate) as epoch SECONDS — a decimal like 1780358755.976 — rather
// than epoch millis or an ISO string (unlike order-service, which pins each
// timestamp with @JsonFormat). Both `Date` and dayjs expect milliseconds for
// numeric input, so feeding the raw value in renders as ~Jan 1970. Convert
// seconds → ms before parsing. Returns null for absent/non-finite input so
// callers can render a placeholder.
export const epochSecondsToDate = (
  seconds: number | null | undefined,
): Date | null => {
  if (seconds == null || !Number.isFinite(seconds)) {
    return null;
  }
  return new Date(seconds * 1000);
};

export const formatDateWithFullMonth = (date: DateInput) => {
  return dayjs(date).format('MMMM D, YYYY');
};

export const convertBooleanValue = (value: boolean | null): string | null => {
  if (value === true) {
    return 'Yes';
  } else if (value === false) {
    return 'No';
  } else {
    return null;
  }
};

export const convertYesOrNoValue = (value: string | null): boolean | null => {
  if (value === 'Yes') {
    return true;
  } else if (value === 'No') {
    return false;
  } else {
    return null;
  }
};

export const extractValue = (str: string | null) => {
  if (str === null || str === '') {
    return '-';
  } else if (str?.includes('Error')) {
    const parts = str.split(':');
    return parts.length > 1 ? parts[1].trim() : '';
  } else {
    return str;
  }
};

// JSON-roundtrip clone. Generic over the input shape, which works because
// every callsite roundtrips JSON-safe data (no Dates, no functions, no
// Maps — those would silently lose fidelity through the stringify/parse).
export const getDeepCopy = <T,>(object: T): T => {
  return JSON.parse(JSON.stringify(object)) as T;
};

export const mapToArray = <V,>(map: Map<string, V>) => {
  return Array.from(map, ([key, value]) => ({ key, value }));
};

// Rounds to at most 2 decimals. Returns a plain number so callers can keep
// doing arithmetic (sums, comparisons) without string concat surprises. For
// display, use formatPriceDisplay.
export const getRoundedPrice = (price: number | null | undefined): number => {
  if (typeof price !== 'number' || !Number.isFinite(price)) {
    return 0;
  }
  return parseFloat(price.toFixed(2));
};

// The one display formatter for money (DV-2935). Always two decimals, always
// thousands-grouped: 125 -> "125.00", 151.2 -> "151.20", 1512.2 ->
// "1,512.20". Whole-dollar amounts used to render bare ("$125" next to
// "$151.20" in the same column), which is the bug this fixes.
//
// Emits digits only — no currency symbol. Every call site writes its own '$',
// so returning one here would produce "$$125.00". `fmtUsd` is the
// symbol-carrying wrapper for callers that want the whole string.
//
// Use this anywhere a price is interpolated into UI; never stringify a raw
// price or a getRoundedPrice() result for display.
export const formatPriceDisplay = (
  price: number | null | undefined,
): string => {
  return getRoundedPrice(price).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const LEADING_PROTOCOL = /^https?:\/\//i;

export const stripUrlProtocol = (value: string): string =>
  value.replace(LEADING_PROTOCOL, '');

export const toHttpsUrl = (value: string): string => {
  const bare = stripUrlProtocol(value.trim());
  return bare ? `https://${bare}` : '';
};

// Rejects URLs with a userinfo component (`user:pass@host`), which is how an
// email address like "name@example.com" sneaks past yup's `.url()` check once
// prefixed with "https://" (the address becomes a syntactically valid URL
// with "name" as the username). Used as a yup `.test()` on website fields.
export const hasNoUrlUserInfo = (value: string | undefined): boolean => {
  if (!value) return true;
  try {
    const url = new URL(value);
    return !url.username && !url.password;
  } catch {
    return true;
  }
};

// * This function is used to format phone number input on the fly
export const formatPhoneNumberInField = (value: string) => {
  // Remove all non-numeric characters
  let numbers = value.replace(/\D/g, '');

  // Allow the user to delete the "1" if it's the only character
  if (numbers === '1' && value.length === 1) {
    return value;
  }

  // Allow optional leading "1" (US country code)
  if (numbers.startsWith('1')) {
    if (numbers.length > 11) {
      numbers = numbers.slice(0, 11); // Max 11 digits if country code is present
    }
  } else {
    if (numbers.length > 10) {
      numbers = numbers.slice(0, 10); // Max 10 digits otherwise
    }
  }

  // Separate the digits
  const match = numbers.match(/^1?(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (!match) return '';

  let formattedNumber = '';

  // Include the country code if present
  if (numbers.startsWith('1')) {
    formattedNumber += '1 ';
  }
  if (match[1]) formattedNumber += `(${match[1]}`;
  if (match[2]) formattedNumber += `) ${match[2]}`;
  if (match[3]) formattedNumber += `-${match[3]}`;

  return formattedNumber;
};

// * This function is used to format phone numbers for visual purposes only
export const formatPhoneNumberDisplay = (value?: string | null) => {
  if (!value) return '';
  // Remove non-numeric characters and leading "1" if present
  let numbers = value.replace(/[^\d]/g, '').replace(/^1/, '');

  // Limit input to 10 digits
  if (numbers.length > 10) {
    numbers = numbers.slice(0, 10);
  }

  // Match number parts
  const match = numbers.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  if (!match) return '';

  let formattedNumber = '';
  if (match[1]) formattedNumber += `(${match[1]}`;
  if (match[2]) formattedNumber += `) ${match[2]}`;
  if (match[3]) formattedNumber += `-${match[3]}`;

  return formattedNumber;
};

/**
 * Break a display address into letter-style lines: street line(s) followed by
 * a single "City, ST Zip" line.
 *
 * Two input shapes, one output shape:
 *  - newline-joined (`getPropertyAddress()`'s output — the properties list's
 *    rule): the lines are already letter-style, so they pass through as-is.
 *  - flat comma-joined ("123 Main St Unit 2, Denver, CO 80202"): the last two
 *    comma segments are always city + "state zip"; everything before them is
 *    street line(s). A string with no commas comes back as a single line.
 *
 * One rule, shared everywhere an address gets split for display (assets page
 * header, Order Service confirmation) so street/locality parsing can't drift
 * between surfaces.
 */
export const toAddressLines = (address: string): string[] => {
  if (address.includes('\n')) {
    return address
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length <= 1) return [address.trim()];
  const cityStateZip = parts.slice(-2).join(', ');
  return [...parts.slice(0, -2), cityStateZip];
};

/**
 * Collapse `toAddressLines` output into the assets-page header's two-line
 * form: every street line (address 1, address 2, unit) comma-joined on the
 * title line, the "City, ST Zip" locality below it — null when the address
 * has nothing to split. One helper for both PropertyMeta sources (the
 * router-state seed and the property fetch) so the header can never switch
 * text between the seeded paint and the fetch resolving.
 */
export const toStreetAndLocality = (
  address: string
): { streetLine: string; localityLine: string | null } => {
  const lines = toAddressLines(address);
  if (lines.length <= 1) {
    return { streetLine: lines[0] ?? address.trim(), localityLine: null };
  }
  return {
    streetLine: lines.slice(0, -1).join(', '),
    localityLine: lines[lines.length - 1]
  };
};

/** A stored text value, trimmed; null when it is missing or only whitespace. */
export const trimmedOrNull = (value: string | null | undefined): string | null =>
  value?.trim() || null;

const STARTS_WITH_LETTER_OR_DIGIT = /^[\p{L}\p{N}]/u;

/**
 * Avatar initials: the first character of the first two words that start with
 * a letter or digit, uppercased, so "(Old) ABC Rentals" gives "AR". Unicode
 * aware, and by code point, so a letter outside the BMP stays whole. '' when no
 * word qualifies; callers pick their own fallback.
 */
export const toInitials = (name: string | null | undefined): string =>
  (name ?? '')
    .split(/\s+/)
    .filter((word) => STARTS_WITH_LETTER_OR_DIGIT.test(word))
    .slice(0, 2)
    .map((word) => Array.from(word)[0].toUpperCase())
    .join('');
